import type { ApiEnvelope } from '../auth/types';

export type RequestFailureKind = 'network' | 'server' | 'parse' | 'unknown';

export class RequestFailure extends Error {
  readonly kind: RequestFailureKind;

  constructor(kind: RequestFailureKind, message: string) {
    super(message);
    this.name = 'RequestFailure';
    this.kind = kind;
  }
}

function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

export async function postJson<TBody extends object>(
  url: string,
  body: TBody,
  signal?: AbortSignal
): Promise<ApiEnvelope> {
  if (isOffline()) {
    throw new RequestFailure(
      'network',
      'No internet connection. Check your network and try again.'
    );
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    if (isOffline()) {
      throw new RequestFailure(
        'network',
        'No internet connection. Check your network and try again.'
      );
    }
    const devHint =
      process.env.NODE_ENV === 'development'
        ? ' Make sure Research-Work-API is running (cd Research-Work-API && npm run dev), then restart the web app.'
        : '';
    throw new RequestFailure(
      'network',
      `Unable to reach the server. It may be down or your connection is unstable.${devHint}`
    );
  }

  if (response.status === 404) {
    throw new RequestFailure(
      'network',
      process.env.NODE_ENV === 'development'
        ? 'API not found. Start Research-Work-API (cd Research-Work-API && npm run dev) on port 8888, then restart the web app.'
        : 'API endpoint not found. Please try again later.'
    );
  }

  if (response.status >= 500) {
    throw new RequestFailure(
      'server',
      'The server is temporarily unavailable. Please try again in a moment.'
    );
  }

  let payload: ApiEnvelope;
  try {
    payload = (await response.json()) as ApiEnvelope;
  } catch {
    throw new RequestFailure(
      'parse',
      'Received an unexpected response from the server.'
    );
  }

  return payload;
}
